/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import {
    object,
} from 'prop-types';
import Media from 'react-media';
import loadable from '@loadable/component';
import mbpLogger from 'mbp-logger';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import style from './SingleProduct.module.css';
import universalHandleClick from '../../../UniversalHandleClick';
import { getBrand } from '../../../../../../pages/Account/state/ducks/Common/Common-Selectors';
import { GRAPHQL_ENV } from '../../../../../../gql';
import useExperimentServiceAttributes from '../../../../../../helpers/experimentService/useExperimentServiceAttributes';
import findCategoryById from '../../../../../../gql/queries/findCategoryById';
import SnipeRender from '../../../../GraphqlProductPage/Partials/common/SnipeRender';
import PriceRange from '../../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/PriceRange';

const MessageStandard = loadable(() => import(/* webpackChunkName: "MessageStandard" */ '../../../SubComponents/Message/MessageStandard'));
const SingleProduct = ({ data }) =>     {
    if (!Object.keys(data)?.length) {
        return mbpLogger.logError({
            appName: process.env.npm_package_name,
            component: 'Features/Standard/SingleProduct',
            message: 'No data available to render component',
        });
    }
    const productData = data?.product_feature?.reference?.[0] || [];
    useStyles(style);
    const defaultParams = 'auto=webp';
    const handleClick = universalHandleClick();
    const singleProductClasses = productData?.invert_layout ? style.rightSideProduct : style.leftSideProduct;
    const productDataLink = productData?.product?.linking;
    const brand = useSelector(getBrand);
    const history = useHistory();
    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCategoryById' });
    const PRODUCTS_QUERY = findCategoryById(isGraphqlTargetingEnabled);
    const variables = {
        brand: brand?.domain,
        environment: GRAPHQL_ENV,
        locale: 'en-us',
        id: productData?.product?.category_reference || '',
        productOptions: {
            pageSize: 1,
            page: 1,
        },
        ...(isGraphqlTargetingEnabled ? { targeting } : {}),
    };
    const { data: categoryData, loading, error } = useQuery(PRODUCTS_QUERY, { variables, context, skip: !productData?.product?.category_reference });
    if (loading) {
        return null;
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            component: 'SingleProduct.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            categoryId: productData?.product?.category_reference,
            error,
        });
        return null;
    }
    const firstProduct = categoryData?.findCategoryById?.products?.[0] || {};
    const productTitle =  firstProduct?.name || productData?.product?.title;
    const productImage = () => {
        if (firstProduct?.image?.name) {
            return `${firstProduct?.image?.path}${firstProduct?.image?.name}x.jpg`;
        }
        return  productData?.product?.image?.url;
    };
    const snipeImage = () => {
        if (firstProduct?.image?.snipe) {
            return `${firstProduct?.image?.snipeImagePath}${firstProduct?.image?.snipe}_z.gif`;
        }
        return '';
    };
    const bannerImage = {
        desktop: productData?.primary_image?.url,
        tablet: productData?.tablet_image?.url || productData?.primary_image?.url,
        mobile: productData?.mobile_image?.url || productData?.tablet_image?.url || productData?.primary_image?.url,
    };
    const device = (matches) => Object.keys(matches).find((key) => matches[key]);
    const renderBanner = (matches) => {
        const mobileView = matches.mobile;
        return (
            <div className={`${style.productContainer} ${!mobileView ? singleProductClasses : ''}`}>
                <div className={style.primaryImageContainer}>
                    <picture>
                        <source srcSet={`${bannerImage[device(matches)]}?${defaultParams}`} />
                        <img src={`${bannerImage[device(matches)]}?${defaultParams}`} alt={productData?.primary_image?.title} loading="lazy" width="692px" height="600px" />
                    </picture>
                </div>
                <div
                    className={`${style.messageContainer} ${!productDataLink?.link?.href ? style.disableClick : ''}`}
                    onClick={() => {
                        if (firstProduct?.seo?.url) {
                            history.push(firstProduct?.seo?.url);
                        } else {
                            handleClick(productDataLink);
                        }
                    }}
                    role="presentation"
                    data-testid="singleProduct"
                >
                    {productTitle &&  !mobileView && <div className={style.heading}> {productTitle}</div>}
                    <div className={style.imageCard}>
                        <picture>
                            <source srcSet={`${productImage()}?${defaultParams}`} />
                            <img src={`${productImage()}?${defaultParams}`} alt={productData?.product?.image?.title} loading="lazy" width="338" height="356px" />
                        </picture>
                        {snipeImage() && (
                            <SnipeRender
                                styleProp={style.snipe}
                                src={snipeImage()}
                            />
                        )}
                    </div>
                    {productTitle &&  mobileView && <div className={style.mobileHeading}> {productTitle}</div>}
                    {firstProduct?.skuPriceRange &&  (
                        <div className={style.productPrice}>
                            <PriceRange
                                skuPriceRange={firstProduct?.skuPriceRange}
                                colorMatchTitle
                                noFromText
                                collectionRestructureEnabled
                            />
                        </div>
                    )}
                    {Object.keys(firstProduct)?.length === 0 && (
                        <MessageStandard
                            data={{
                                heading: mobileView ? productData?.product?.title : '',
                                sub_heading: productData?.product?.sub_title,
                                cta: {
                                    link: {
                                        href: productData?.product?.linking.link.href,
                                        title: productData?.product?.linking.link.title,
                                    },
                                    presentation: 'text',
                                    tracking_event_category: productData?.product?.linking.tracking_event_category,
                                    tracking_event_action: productData?.product?.linking.tracking_event_action,
                                    tracking_event_label: productData?.product?.linking.tracking_event_label,
                                },
                                has_background: false,
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };
    return (
        <>
            <Media queries={{
                desktop: '(min-width:1024px)',
                tablet: '(min-width: 600.5px) and (max-width: 1023.5px)',
                mobile: '(max-width: 600px)',
            }}
            >
                {(matches) => renderBanner(matches)}
            </Media>
        </>
    );
};
SingleProduct.propTypes = {
    data: object.isRequired,
};
export default SingleProduct;
